import { Portal } from '@mui/material';
import * as React from 'react';
import { TooltipDirection, TooltipSize, TooltipTheme } from './tooltip';
import * as S from './tooltip-entity.styles';

export interface ITooltipEntityProps {
    open: boolean;
    anchor: React.RefObject<HTMLElement | SVGElement>;
    direction: TooltipDirection;
    size?: TooltipSize;
    variant?: TooltipTheme;
    children?: React.ReactNode;
}
export interface ITooltipEntityState {
    anchorRect?: ClientRect;
    viewportRect?: ClientRect;
}

export class TooltipEntity extends React.Component<ITooltipEntityProps, ITooltipEntityState> {
    constructor(props: ITooltipEntityProps) {
        super(props);
        this.state = {
            anchorRect: undefined,
            viewportRect: undefined,
        };
    }
    public render(): React.ReactNode {
        return <Portal container={document.body}>{this.renderEntity()}</Portal>;
    }
    public componentDidMount(): void {
        if (this.props.open) {
            this.updateRect();
        }
        window.addEventListener('resize', this.updateRect);
    }
    public componentWillUnmount(): void {
        window.removeEventListener('resize', this.updateRect);
    }
    public componentDidUpdate(prevProps: ITooltipEntityProps): void {
        // Remeasure only when the tooltip opens. Measuring after our own setState
        // (a top-of-page tooltip can toggle a document scrollbar and move the
        // anchor) is React error #185: Maximum update depth exceeded.
        if (this.props.open && !prevProps.open) {
            this.updateRect();
        }
    }
    private updateRect = (): void => {
        if (!this.props.open || !this.props.anchor?.current) {
            return;
        }
        const viewportRect = document.body.getBoundingClientRect();
        const anchorRect = this.props.anchor.current.getBoundingClientRect();
        const prevAnchor = this.state.anchorRect;
        const prevViewport = this.state.viewportRect;
        if (
            prevAnchor &&
            prevViewport &&
            Math.round(prevAnchor.left) === Math.round(anchorRect.left) &&
            Math.round(prevAnchor.top) === Math.round(anchorRect.top) &&
            Math.round(prevAnchor.width) === Math.round(anchorRect.width) &&
            Math.round(prevAnchor.height) === Math.round(anchorRect.height) &&
            Math.round(prevViewport.width) === Math.round(viewportRect.width)
        ) {
            return;
        }
        this.setState({
            anchorRect,
            viewportRect,
        });
    };
    private renderEntity = (): React.ReactElement => {
        if (this.props.open && this.state.anchorRect && this.state.viewportRect) {
            return (
                <S.Entity
                    direction={this.props.direction}
                    size={this.props.size}
                    variant={this.props.variant}
                    anchorRect={this.state.anchorRect}
                    viewportRect={this.state.viewportRect}>
                    {this.props.children}
                </S.Entity>
            );
        }
        return <></>;
    };
}
